import React, { useState } from 'react';
import {
  ChevronLeft, ChevronRight, MessageCircle, Users, Search, Hash,
  Lock, UserPlus, Plus, X
} from 'lucide-react';

type ActiveTab = 'dms' | 'groups';

interface Props {
  onSignupPrompt: () => void;
}

const DEMO_DMS = [
  { id: '1', firstName: 'Alexis', lastName: 'Carter', initials: 'AC', lastMessage: 'Great working with you on the HCM module!', lastMessageAt: '2m', unread: 2 },
  { id: '2', firstName: 'Marcus', lastName: 'Webb', initials: 'MW', lastMessage: 'Can you share the integration notes?', lastMessageAt: '1h', unread: 0 },
  { id: '3', firstName: 'Priya', lastName: 'Nair', initials: 'PN', lastMessage: 'The payroll config is done.', lastMessageAt: '3h', unread: 1 },
];

const DEMO_GROUPS = [
  { id: '1', name: 'Workday HCM Team', memberCount: 8, lastMessage: 'Config freeze is Friday', lastMessageAt: '5m' },
  { id: '2', name: 'Integration Leads', memberCount: 5, lastMessage: 'API docs updated', lastMessageAt: '2h' },
  { id: '3', name: 'Community Announcements', memberCount: 24, lastMessage: 'Welcome to the network!', lastMessageAt: '1d' },
];

const AVATAR_COLORS = ['bg-[#40E0D0]', 'bg-teal-500', 'bg-sky-500', 'bg-emerald-500', 'bg-cyan-600'];

function DemoAvatar({ initials, index, size = 10 }: { initials: string; index: number; size?: number }) {
  const color = AVATAR_COLORS[index % AVATAR_COLORS.length];
  return (
    <div
      className={`w-${size} h-${size} rounded-full ${color} flex items-center justify-center text-white font-semibold flex-shrink-0`}
      style={{ fontSize: size <= 8 ? '0.7rem' : '0.85rem' }}
    >
      {initials}
    </div>
  );
}

export default function PublicChatPreview({ onSignupPrompt }: Props) {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<ActiveTab>('dms');
  const [search, setSearch] = useState('');
  const [activeDemo, setActiveDemo] = useState<string | null>(null);

  const handleInteract = () => {
    onSignupPrompt();
  };

  const filteredDms = DEMO_DMS.filter(
    (d) => search === '' || `${d.firstName} ${d.lastName}`.toLowerCase().includes(search.toLowerCase())
  );

  const filteredGroups = DEMO_GROUPS.filter(
    (g) => search === '' || g.name.toLowerCase().includes(search.toLowerCase())
  );

  const renderEmptyState = () => (
    <div className="flex flex-col items-center justify-center h-full gap-4 px-6 py-8 text-center">
      <div className="w-16 h-16 rounded-full bg-[#40E0D0] bg-opacity-10 flex items-center justify-center">
        {activeTab === 'dms'
          ? <MessageCircle className="w-8 h-8 text-[#40E0D0]" />
          : <Users className="w-8 h-8 text-[#40E0D0]" />}
      </div>
      <div>
        <p className="text-sm font-semibold text-gray-900 mb-1">
          {activeTab === 'dms' ? 'Direct Messages' : 'Group Chats'}
        </p>
        <p className="text-xs text-gray-500 leading-relaxed">
          {activeTab === 'dms'
            ? 'Message other Workday consultants privately once you join the community.'
            : 'Create and join group chats with your team and connections.'}
        </p>
      </div>
      <button
        onClick={handleInteract}
        className="flex items-center gap-2 px-4 py-2 bg-[#40E0D0] hover:bg-[#35c5b5] text-white text-xs font-medium rounded-lg transition-colors"
      >
        <UserPlus className="w-3.5 h-3.5" />
        Join to unlock
      </button>
    </div>
  );

  const renderDmList = () => (
    <div className="flex-1 overflow-y-auto">
      {filteredDms.length === 0 ? renderEmptyState() : (
        <>
          {filteredDms.map((dm, idx) => (
            <button
              key={dm.id}
              onClick={handleInteract}
              className={`w-full flex items-center gap-3 px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors text-left relative ${
                activeDemo === dm.id ? 'bg-[#e6faf9]' : ''
              }`}
              onMouseEnter={() => setActiveDemo(dm.id)}
              onMouseLeave={() => setActiveDemo(null)}
            >
              <div className="relative flex-shrink-0">
                <DemoAvatar initials={dm.initials} index={idx} size={10} />
                {dm.unread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-[#40E0D0] text-white text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center">
                    {dm.unread}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900 truncate">{dm.firstName} {dm.lastName}</span>
                  <span className="text-xs text-gray-400 flex-shrink-0 ml-2">{dm.lastMessageAt}</span>
                </div>
                <p className={`text-xs truncate mt-0.5 ${dm.unread > 0 ? 'font-medium text-gray-900' : 'text-gray-500'}`}>
                  {dm.lastMessage}
                </p>
              </div>
              <Lock className="w-3 h-3 text-gray-300 flex-shrink-0" />
            </button>
          ))}
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
            <button
              onClick={handleInteract}
              className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border-2 border-dashed border-[#40E0D0] text-[#40E0D0] text-xs font-medium hover:bg-[#e6faf9] transition-colors"
            >
              <UserPlus className="w-3.5 h-3.5" />
              Sign up to message consultants
            </button>
          </div>
        </>
      )}
    </div>
  );

  const renderGroupList = () => (
    <div className="flex-1 overflow-y-auto">
      <button
        onClick={handleInteract}
        className="w-full flex items-center gap-3 px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors text-[#40E0D0]"
      >
        <div className="w-10 h-10 rounded-full border-2 border-dashed border-[#40E0D0] flex items-center justify-center flex-shrink-0">
          <Plus className="w-4 h-4" />
        </div>
        <span className="text-sm font-medium">Create new group</span>
      </button>

      {filteredGroups.map((group, idx) => (
        <button
          key={group.id}
          onClick={handleInteract}
          className={`w-full flex items-center gap-3 px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors text-left relative ${
            activeDemo === group.id ? 'bg-[#e6faf9]' : ''
          }`}
          onMouseEnter={() => setActiveDemo(group.id)}
          onMouseLeave={() => setActiveDemo(null)}
        >
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#40E0D0] to-[#2ab5a8] flex items-center justify-center flex-shrink-0">
            <Hash className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-900 truncate">{group.name}</span>
              <span className="text-xs text-gray-400 flex-shrink-0 ml-2">{group.lastMessageAt}</span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5 truncate">
              {group.memberCount} members · {group.lastMessage}
            </p>
          </div>
          <Lock className="w-3 h-3 text-gray-300 flex-shrink-0" />
        </button>
      ))}

      <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
        <button
          onClick={handleInteract}
          className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border-2 border-dashed border-[#40E0D0] text-[#40E0D0] text-xs font-medium hover:bg-[#e6faf9] transition-colors"
        >
          <UserPlus className="w-3.5 h-3.5" />
          Sign up to join group chats
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Toggle tab */}
      <div
        className={`fixed z-40 flex items-center transition-all duration-300 ease-in-out ${
          open ? 'right-[360px]' : 'right-0'
        }`}
        style={{ top: '50%', transform: 'translateY(-50%)', marginTop: '48px' }}
      >
        <button
          onClick={() => setOpen((v) => !v)}
          className="relative flex items-center justify-center w-8 h-20 rounded-l-xl shadow-xl border border-r-0 bg-white border-gray-200 hover:bg-gray-50 text-gray-600 transition-colors"
          title={open ? 'Hide chat' : 'Open messages'}
        >
          <span className="absolute -top-1.5 -left-1.5 bg-[#40E0D0] text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
            {DEMO_DMS.reduce((s, d) => s + d.unread, 0)}
          </span>
          {open ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </div>

      {/* Panel */}
      <div
        className={`fixed right-0 bottom-0 z-30 flex flex-col border-l border-gray-200 shadow-2xl bg-white transition-all duration-300 ease-in-out ${
          open ? 'w-[360px]' : 'w-0 overflow-hidden'
        }`}
        style={{ top: '96px', maxWidth: '100vw' }}
      >
        {open && (
          <>
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-[#40E0D0]" />
                <span className="font-semibold text-sm text-gray-900">Messages</span>
                <span className="bg-[#40E0D0] text-white text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
                  {DEMO_DMS.reduce((s, d) => s + d.unread, 0)}
                </span>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-1 rounded-md hover:bg-gray-100 text-gray-500 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200">
              {(['dms', 'groups'] as ActiveTab[]).map((tab) => (
                <button
                  key={tab}
                  onClick={() => { setActiveTab(tab); setSearch(''); }}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${
                    activeTab === tab
                      ? 'border-b-2 border-[#40E0D0] text-[#40E0D0]'
                      : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  {tab === 'dms' ? <MessageCircle className="w-3.5 h-3.5" /> : <Users className="w-3.5 h-3.5" />}
                  {tab === 'dms' ? 'Direct' : 'Groups'}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="px-3 py-2 border-b border-gray-200">
              <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-gray-100">
                <Search className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={activeTab === 'dms' ? 'Search connections…' : 'Search groups…'}
                  className="flex-1 bg-transparent text-xs outline-none text-gray-900 placeholder:text-gray-400"
                />
              </div>
            </div>

            {/* Preview banner */}
            <div className="px-3 py-2 bg-[#40E0D0] bg-opacity-10 border-b border-[#40E0D0] border-opacity-20">
              <p className="text-xs text-teal-700 text-center">
                <Lock className="w-3 h-3 inline mr-1" />
                Preview mode &mdash;{' '}
                <button onClick={handleInteract} className="underline font-semibold hover:text-teal-900 transition-colors">
                  Sign up free
                </button>{' '}
                to start messaging
              </p>
            </div>

            {activeTab === 'dms' ? renderDmList() : renderGroupList()}
          </>
        )}
      </div>
    </>
  );
}
